import { MediaCollection, ModelType } from "@/gql/graphql";
import { FileUploader, formatBytes } from "@/lib/FileUpload/FileUploader";
import { useGqlMutation } from "@/lib/GraphQLCodegen/fetcher";
import {
  createMediaMutation,
  deleteMediaMutation,
} from "@/app/Media/GraphQL/mediaMutations";
import { Button } from "@/components/catalyst/button";
import { DocumentIcon, TrashIcon } from "@heroicons/react/24/outline";
import { toast } from "sonner";
import { useConfirmDeleteDialog } from "@/lib/Components/Dialog/Hooks/useConfirmDeleteDialog";
import { useWorkOrderAssignment } from "@/app/ProDrivers/WorkOrders/Hooks/useWorkOrderAssignment";
import { formatDate } from "@/lib/Formatters/formatDate";

export function AssignmentFileUploadForm({
  assignmentId,
}: {
  assignmentId: string;
}) {
  const assignment = useWorkOrderAssignment(assignmentId);
  const files = assignment.files;

  const { open: openConfirmDelete } = useConfirmDeleteDialog();

  const { mutateAsync: createMedia } = useGqlMutation(createMediaMutation);
  const { mutateAsync: deleteMedia } = useGqlMutation(deleteMediaMutation);

  function handleDelete(fileId: string) {
    openConfirmDelete({
      title: "Delete File",
      message: "Are you sure you want to delete this file?",
      onDelete: async () => {
        try {
          await deleteMedia({ id: fileId });
          toast.success("File deleted");
        } catch {
          toast.error("Failed to delete file");
        }
      },
    });
  }

  return (
    <div className="space-y-4">
      {files.length > 0 ? (
        <ul className="divide-y divide-gray-200 rounded-lg border border-gray-200">
          {files.map((file) => (
            <li
              key={file.id}
              className="flex items-center gap-3 px-3 py-2 text-sm hover:bg-gray-50"
            >
              <a
                href={file.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex min-w-0 flex-1 items-center gap-3"
              >
                <DocumentIcon className="size-5 flex-shrink-0 text-gray-400" />
                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium text-gray-900 hover:underline">
                    {file.file_name}
                  </p>
                  <p className="text-xs text-gray-500">
                    {formatBytes(file.size)} · {formatDate(file.created_at)}
                  </p>
                </div>
              </a>
              <Button
                color="red"
                type="button"
                onClick={() => handleDelete(file.id)}
                LeadingIcon={TrashIcon}
              />
            </li>
          ))}
        </ul>
      ) : null}

      <FileUploader
        maxFileCount={10}
        maxSize={1024 * 1024 * 25}
        accept={{
          "application/pdf": [],
          "application/msword": [],
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
            [],
          "application/vnd.ms-excel": [],
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
            [],
          "text/plain": [],
          "text/csv": [],
        }}
        fileTypeLabel="Supports PDF, Word, Excel, and text documents"
        onUpload={async (uploads) => {
          const results = await Promise.allSettled(
            uploads.map((upload) =>
              createMedia({
                input: {
                  file_name: upload.file.name,
                  objectId: assignmentId,
                  objectType: ModelType.WorkOrderAssignment,
                  collectionName: MediaCollection.AssignmentFile,
                  key: upload.filePath,
                },
              }),
            ),
          );

          const succeeded = results.filter(
            (r) => r.status === "fulfilled",
          ).length;
          const failed = results.length - succeeded;

          if (succeeded > 0) {
            toast.success(
              succeeded === 1 ? "File uploaded" : `${succeeded} files uploaded`,
            );
          }
          if (failed > 0) {
            toast.error(
              failed === 1
                ? "Failed to upload file"
                : `Failed to upload ${failed} files`,
            );
          }
        }}
      />
    </div>
  );
}
